import React, { useState } from 'react';
import {
  Undo2, Barcode, Package, CheckCircle2,
  ChevronRight, ChevronLeft, Plus, Minus, Trash2, AlertCircle,
} from 'lucide-react';
import { Product, ProductSize } from '../../types';
import WizardProductSelector, { WizardItem } from './WizardProductSelector';

interface WarehouseTransferViewProps {
  products: Product[];
  onComplete: (payload: {
    items: { product: Product; size: ProductSize; quantity: number }[];
    details: { recipient: string };
  }) => Promise<void>;
}

const steps = [
  { label: 'Pick', icon: Barcode },
  { label: 'Audit', icon: Package },
  { label: 'Confirm', icon: CheckCircle2 },
];

const WarehouseTransferView: React.FC<WarehouseTransferViewProps> = ({ products, onComplete }) => {
  const [step, setStep] = useState(0);
  const [selectedItems, setSelectedItems] = useState<WizardItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const reset = () => {
    setStep(0);
    setSelectedItems([]);
    setError(null);
  };

  const updateQty = (idx: number, delta: number) => {
    setSelectedItems(prev => {
      const next = [...prev];
      const item = next[idx];
      const newQty = item.quantity + delta;
      if (newQty < 1) return prev;
      if (newQty > item.size.stock) {
        setError(`Max store stock available is ${item.size.stock}`);
        return prev;
      }
      next[idx] = { ...item, quantity: newQty };
      setError(null);
      return next;
    });
  };

  const removeItem = (idx: number) => {
    setSelectedItems(prev => prev.filter((_, i) => i !== idx));
  };

  const handleContinue = () => {
    if (step === 0) {
      if (selectedItems.length === 0) { setError('Select items to transfer.'); return; }
      setError(null);
      setStep(1);
    } else {
      setError(null);
      setStep(s => s + 1);
    }
  };

  // BUG #8b fix: await onComplete so success toast fires before the wizard resets
  const handleFinish = async () => {
    if (selectedItems.length === 0) { setError('No items to transfer.'); setStep(0); return; }
    setIsSubmitting(true);
    try {
      await onComplete({ items: selectedItems, details: { recipient: 'Main Warehouse' } });
      reset();
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalUnits = selectedItems.reduce((a, b) => a + b.quantity, 0);

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto p-8">
      <div className="max-w-2xl mx-auto space-y-6">

        {/* Header */}
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Undo2 size={22} className="text-cyan-500" />
              Warehouse Transfer
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Move store stock back to the warehouse.
            </p>
          </div>
          {step > 0 && (
            <button
              onClick={reset}
              className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 rounded-xl text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
            >
              Start Over
            </button>
          )}
        </header>

        {/* Step indicator */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
          <div className="flex items-center justify-between relative">
            {steps.map((s, idx) => (
              <div key={idx} className="flex flex-col items-center gap-1.5 flex-1 relative">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center z-10 transition-all duration-300 ${
                  step > idx
                    ? 'bg-cyan-600 text-white'
                    : step === idx
                    ? 'bg-cyan-600 text-white ring-4 ring-cyan-100 dark:ring-cyan-900/30'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-400'
                }`}>
                  <s.icon size={14} />
                </div>
                <span className={`text-[9px] font-black uppercase tracking-widest ${
                  step >= idx ? 'text-cyan-600' : 'text-gray-400'
                }`}>{s.label}</span>
                {idx < steps.length - 1 && (
                  <div className={`absolute left-1/2 top-4 w-full h-[2px] -z-0 transition-colors duration-300 ${
                    step > idx ? 'bg-cyan-600' : 'bg-gray-100 dark:bg-gray-700'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div className="flex items-center gap-2.5 p-3 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/40 rounded-xl">
            <AlertCircle size={14} className="text-red-500 shrink-0" />
            <p className="text-xs font-bold text-red-600 dark:text-red-400 uppercase tracking-wide">{error}</p>
          </div>
        )}

        {/* Step 0 — Pick */}
        {step === 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
            <div className="px-6 pt-5 pb-2 border-b border-gray-50 dark:border-gray-700/50">
              <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">
                Search or scan items to move to warehouse
              </p>
            </div>
            <div className="p-4">
              <WizardProductSelector
                products={products}
                selectedItems={selectedItems}
                onUpdateItems={setSelectedItems}
                stockLocation="stock"
              />
            </div>
          </div>
        )}

        {/* Step 1 — Audit */}
        {step === 1 && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
            <div className="px-6 pt-5 pb-3 border-b border-gray-50 dark:border-gray-700/50 flex items-center justify-between">
              <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">
                Transfer Audit — {selectedItems.length} line(s)
              </p>
              <button
                onClick={() => setStep(0)}
                className="flex items-center gap-1 text-[9px] font-black text-cyan-600 uppercase"
              >
                <Plus size={11} /> Add More
              </button>
            </div>
            <div className="p-4 space-y-2">
              {selectedItems.map((item, idx) => (
                <div key={idx} className="bg-gray-50 dark:bg-gray-900/50 p-3 rounded-xl border border-gray-100 dark:border-gray-800 flex items-center gap-3">
                  <img src={item.product.image} className="w-12 h-12 rounded-lg object-cover shadow-sm shrink-0" alt={item.product.name} />
                  <div className="flex-1 min-w-0">
                    <h4 className="text-[10px] font-black dark:text-white uppercase truncate">{item.product.name}</h4>
                    <p className="text-[8px] font-bold text-gray-400">Size {item.size.size} · Store stock: {item.size.stock}</p>
                  </div>
                  <div className="flex items-center gap-2 bg-white dark:bg-gray-800 p-1 rounded-lg border border-gray-100 dark:border-gray-700">
                    <button onClick={() => updateQty(idx, -1)} className="p-0.5 text-gray-400 hover:text-red-500 transition-colors">
                      <Minus size={12} />
                    </button>
                    <span className="text-[10px] font-black dark:text-white w-5 text-center">{item.quantity}</span>
                    <button onClick={() => updateQty(idx, 1)} className="p-0.5 text-gray-400 hover:text-cyan-500 transition-colors">
                      <Plus size={12} />
                    </button>
                  </div>
                  <button onClick={() => removeItem(idx)} className="p-1 text-gray-300 hover:text-red-500 transition-colors">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 2 — Confirm */}
        {step === 2 && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-8 text-center space-y-5">
            <div className="w-14 h-14 bg-cyan-50 dark:bg-cyan-900/20 text-cyan-600 rounded-2xl flex items-center justify-center mx-auto border border-cyan-100 dark:border-cyan-800/40">
              <CheckCircle2 size={28} />
            </div>
            <div>
              <h4 className="text-xl font-black text-gray-900 dark:text-white uppercase tracking-tight">Confirm Transfer</h4>
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">
                Authorizing movement of {totalUnits} unit(s) → Warehouse
              </p>
            </div>

            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-dashed border-gray-200 dark:border-gray-700 p-4 text-left space-y-2 max-h-48 overflow-y-auto">
              <div className="flex justify-between items-center pb-2 border-b border-gray-100 dark:border-gray-800">
                <span className="text-[8px] font-black text-gray-400 uppercase">Target</span>
                <span className="text-[10px] font-black text-cyan-600 uppercase">Warehouse Stock</span>
              </div>
              {selectedItems.map((item, idx) => (
                <div key={idx} className="flex justify-between items-center text-[9px] py-1 border-b border-gray-50 dark:border-gray-800 last:border-0">
                  <span className="font-bold dark:text-white truncate max-w-[200px]">{item.product.name} (Size {item.size.size})</span>
                  <span className="font-black text-cyan-500">×{item.quantity}</span>
                </div>
              ))}
            </div>

            <div className="flex items-start gap-2.5 p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-800/30 rounded-xl text-left">
              <AlertCircle size={13} className="text-amber-500 mt-0.5 shrink-0" />
              <p className="text-[9px] font-bold text-amber-700 dark:text-amber-400 uppercase leading-relaxed">
                This will decrease store stock and increase warehouse stock immediately upon authorization.
              </p>
            </div>
          </div>
        )}

      </div>
      </div>{/* end scrollable */}

      {/* Sticky navigation — always at eye level */}
      <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-8 py-4">
        <div className="max-w-2xl mx-auto flex gap-3">
          {step > 0 && (
            <button
              onClick={() => setStep(s => s - 1)}
              className="flex-1 py-3 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-300 rounded-xl font-black text-[10px] uppercase tracking-widest border border-gray-200 dark:border-gray-700 hover:bg-gray-50 transition-all flex items-center justify-center gap-1.5"
            >
              <ChevronLeft size={14} /> Back
            </button>
          )}
          <button
            onClick={step === 2 ? handleFinish : handleContinue}
            disabled={isSubmitting}
            className="flex-[2] py-3 bg-cyan-600 text-white rounded-xl font-black text-[10px] uppercase tracking-[0.15em] shadow-lg shadow-cyan-100 dark:shadow-none hover:bg-cyan-700 active:scale-95 transition-all flex items-center justify-center gap-1.5 group disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <><div className="w-3 h-3 rounded-full border-2 border-white/30 border-t-white animate-spin" /> Processing</>
            ) : (
              <>{step === 2 ? 'Authorize Transfer' : 'Continue'}{step < 2 && <ChevronRight size={14} className="group-hover:translate-x-0.5 transition-transform" />}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WarehouseTransferView;
